import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGame } from "@/context/GameContext";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { flagUrl } from "@/lib/utils";
import { useOpenPlayer } from "@/components/game/PlayerCard";
import { MessageSquare, Users, Shield, Send, Circle } from "lucide-react";

const CHANNELS = [
  { id: "world", label: "World", icon: MessageSquare },
  { id: "clan",  label: "Clan",  icon: Shield },
];

function TimeStamp({ iso }) {
  const t = new Date(iso);
  return (
    <span className="text-[9px] font-numeric text-stone-600" title={iso}>
      {t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
    </span>
  );
}

export function ChatPanel() {
  const { chat, online, sendChat, snap } = useGame();
  const openPlayer = useOpenPlayer();
  const [channel, setChannel] = useState("world");
  const [text, setText] = useState("");
  const [dmTarget, setDmTarget] = useState(null); // {id, name}
  const [dmList, setDmList] = useState([]); // friends usable for DM
  const [historyByChannel, setHistoryByChannel] = useState({});
  const boxRef = useRef(null);

  const activeChannelKey = channel === "dm" && dmTarget ? `dm:${dmTarget.id}` : channel;

  // Load friends for DM tab.
  useEffect(() => {
    api.get("/api/game/social/friends")
      .then(({ data }) => setDmList(data.friends))
      .catch(() => {});
  }, []);

  // Pull recent messages per selected channel.
  const loadRecent = useCallback(async () => {
    const path = channel === "dm" && dmTarget ? `dm:${dmTarget.id}` : channel;
    try {
      const { data } = await api.get(`/api/game/social/chat/${encodeURIComponent(path)}`);
      setHistoryByChannel((prev) => ({ ...prev, [path]: data.messages }));
    } catch (e) {
      if (channel !== "clan") toast.error(formatApiError(e));
    }
  }, [channel, dmTarget]);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  // World messages arrive via GameContext `chat` array (world channel only).
  // For other channels we merge in the WS-delivered ones from the local
  // `chat` array too, filtering by channel field.
  const merged = useMemo(() => {
    const key = activeChannelKey;
    const stored = historyByChannel[key] || [];
    const live = chat.filter((m) => {
      if (channel === "world")   return !m.channel || m.channel === "world";
      if (channel === "clan")    return (m.channel || "").startsWith("clan:");
      if (channel === "dm" && dmTarget) {
        const c = m.channel || "";
        return c === `dm:${snap?.user_id}|${dmTarget.id}`
            || c === `dm:${dmTarget.id}|${snap?.user_id}`;
      }
      return false;
    });
    const seen = new Set();
    const out = [];
    for (const m of [...stored, ...live]) {
      const k = `${m.created_at}|${m.author_id}|${m.text}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(m);
    }
    return out.slice(-100);
  }, [historyByChannel, chat, channel, dmTarget, activeChannelKey, snap?.user_id]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [merged.length]);

  async function submit(e) {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const payload = { text: text.trim(), channel: channel === "dm" && dmTarget ? `dm:${dmTarget.id}` : channel };
      await api.post("/api/game/social/chat/send", payload);
      setText("");
      loadRecent();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  const inClan = !!snap?.clan_id;
  const disabled = channel === "clan" && !inClan;

  return (
    <div className="p-4 lg:p-6 space-y-3" data-testid="chat-panel">
      <div
        className="panel overflow-hidden relative"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(12,10,9,0.55), rgba(12,10,9,0.92)), url(/generated/hero_landing.png)`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="p-4 sm:p-6 flex items-center gap-3 sm:gap-6">
          <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 rounded-lg border-2 border-amber-700 bg-stone-950 flex items-center justify-center">
            <MessageSquare size={26} className="text-amber-400 sm:hidden" />
            <MessageSquare size={30} className="text-amber-400 hidden sm:block" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl sm:text-4xl heading-gilded truncate">Chat</h1>
            <p className="text-stone-400 font-body italic text-xs sm:text-sm hidden sm:block">Three channels: the shared aether, your clan hall, and private whispers.</p>
            <div className="text-xs sm:text-sm font-numeric text-stone-300 mt-1">
              {online} online across the world
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        {/* Sidebar: channels + DM targets */}
        <aside className="panel p-3 space-y-3">
          <div>
            <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 mb-1.5">Channels</h3>
            <div className="space-y-1">
              {CHANNELS.map((c) => {
                const Icon = c.icon;
                const isActive = channel === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => { setChannel(c.id); setDmTarget(null); }}
                    data-testid={`chan-${c.id}`}
                    className={
                      "w-full flex items-center gap-2 px-2 py-1.5 rounded border text-sm transition-colors " +
                      (isActive
                        ? "bg-amber-900/40 border-amber-700 text-amber-200"
                        : "bg-stone-900 border-stone-800 text-stone-300 hover:border-amber-800")
                    }
                  >
                    <Icon size={13} /> {c.label}
                    {c.id === "clan" && !inClan && (
                      <span className="ml-auto text-[9px] text-stone-500">no clan</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 mb-1.5">
              Whispers
            </h3>
            {dmList.length === 0 ? (
              <div className="text-[11px] italic text-stone-500 py-1">
                Add friends to start private whispers.
              </div>
            ) : (
              <div className="space-y-1" data-testid="dm-friends-list">
                {dmList.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => { setChannel("dm"); setDmTarget({ id: f.id, name: f.name }); }}
                    data-testid={`dm-friend-${f.id}`}
                    className={
                      "w-full flex items-center gap-2 px-2 py-1 rounded border text-sm transition-colors " +
                      (channel === "dm" && dmTarget?.id === f.id
                        ? "bg-amber-900/40 border-amber-700 text-amber-200"
                        : "bg-stone-900 border-stone-800 text-stone-300 hover:border-amber-800")
                    }
                  >
                    <Circle size={7} className={f.online ? "fill-emerald-400 text-emerald-400" : "fill-stone-600 text-stone-600"} strokeWidth={0} />
                    <span className="truncate">{f.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>

        {/* Main chat pane */}
        <section className="panel p-3 lg:col-span-3 flex flex-col h-[65vh] lg:h-[560px]">
          <div className="flex items-center justify-between mb-2">
            <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500">
              {channel === "world" && "World"}
              {channel === "clan" && (inClan ? "Clan Hall" : "Clan Hall (locked)")}
              {channel === "dm" && dmTarget && `Whisper · ${dmTarget.name}`}
              {channel === "dm" && !dmTarget && "Whisper"}
            </h3>
            <div className="text-[10px] font-numeric text-stone-500">{merged.length} messages</div>
          </div>
          <div ref={boxRef} className="flex-1 overflow-y-auto scroll-thin space-y-1.5 pr-1" data-testid="chat-messages">
            {merged.length === 0 && (
              <div className="text-stone-500 italic font-body text-sm py-6 text-center">
                {disabled
                  ? "Join a clan to unlock clan chat."
                  : channel === "dm" && !dmTarget
                    ? "Pick a friend in the sidebar to whisper to."
                    : "No one is speaking yet. Say hello."}
              </div>
            )}
            {merged.map((m, i) => (
              <div key={i} className="text-sm font-body flex items-start gap-2" data-testid={`msg-${i}`}>
                <TimeStamp iso={m.created_at} />
                {m.author_iso_a2 && (
                  <img
                    src={flagUrl(m.author_iso_a2, 20)}
                    alt={m.author_iso_a3 || m.author_iso_a2}
                    title={m.author_iso_a3 || m.author_iso_a2}
                    loading="lazy"
                    className="w-4 h-3 object-cover rounded-[2px] shadow-[0_0_0_1px_rgba(0,0,0,0.6)] mt-1 shrink-0"
                    data-testid={`msg-${i}-flag`}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                )}
                <button
                  type="button"
                  onClick={() => m.author_id && openPlayer(m.author_id)}
                  disabled={!m.author_id}
                  className={
                    "font-numeric shrink-0 " +
                    (m.author_id
                      ? "text-amber-400 hover:text-amber-200 hover:underline underline-offset-2 transition-colors cursor-pointer"
                      : "text-amber-400")
                  }
                  data-testid={`msg-${i}-author`}
                >
                  {m.author}:
                </button>
                <span className="text-stone-200 break-words">{m.text}</span>
              </div>
            ))}
          </div>
          <form onSubmit={submit} className="mt-3 flex gap-2" data-testid="chat-form">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={200}
              disabled={disabled || (channel === "dm" && !dmTarget)}
              placeholder={
                disabled ? "Join a clan first…" :
                channel === "dm" && !dmTarget ? "Choose a friend to whisper…" :
                "Speak into the aether…"
              }
              className="flex-1 bg-stone-950 border border-stone-700 rounded px-3 py-2 focus:border-amber-600 outline-none font-body disabled:opacity-50"
              data-testid="chat-input"
            />
            <button type="submit" className="btn-gold px-4 flex items-center gap-1" data-testid="chat-send" disabled={disabled}>
              <Send size={14} />
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
